@use '../../styles/util' as *;
@use '../../styles/global' as *;

.sol-button {
  display: inline-flex;
  width: var(--sol-button-width, auto);
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-default, 3px);
  cursor: pointer;

  /* stylelint-disable-next-line property-no-unknown */
  font-smooth: never;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  &-primary {
    @include base-layer(var(--sol-button-background-color, var(--surface-accent-default)));

    color: var(--text-base-reversed-persistent);

    &:hover::before {
      @include before-layer(var(--surface-utilities-overlays-reversed-hover), var(--radius-default));
    }

    &:active::before {
      @include before-layer(var(--surface-utilities-overlays-reversed-pressed), var(--radius-default));

      outline: none;
    }

    &:focus {
      outline: 2px solid var(--stroke-interaction);
      outline-offset: 2px;
    }

    sol-icon {
      color: var(--icon-interaction-reversed);
    }

    &.sol-button--no-text {
      sol-icon {
        color: var(--icon-base-reversed-persistent);
      }
    }

    &.sol-button--no-outline:focus {
      color: var(--text-base-reversed-persistent);
    }
  }

  &-secondary {
    @include base-layer(var(--sol-button-background-color, var(--surface-base-secondary-alpha)));

    color: var(--text-base-default);

    &:hover::before {
      @include before-layer(var(--surface-utilities-overlays-hover), var(--radius-default));

      border-radius: var(--radius-default, 3px);
    }

    &:active::before {
      @include before-layer(var(--surface-utilities-overlays-pressed), var(--radius-default));

      border-radius: var(--radius-default, 3px);
      outline: none;
    }

    &:focus {
      border: none;
      color: var(--text-interaction-default);
      outline: 2px solid var(--stroke-interaction);
      outline-offset: 2px;

      sol-icon {
        color: var(--stroke-interaction, #0c7ba1);
      }
    }

    &.sol-button--is-opaque {
      @include base-layer(var(--sol-button-background-color, var(--surface-base-secondary)));
    }
  }

  &-outlined {
    @include base-layer(var(--sol-button-background-color, var(--surface-base-default)));

    border-radius: var(--radius-default, 3px);
    box-shadow: inset 0 0 0 1px var(--stroke-default);
    color: var(--text-base-default);

    &:hover,
    &:active,
    &:focus,
    &.sol-button--disabled {
      box-shadow: none;
    }

    &:hover::before {
      @include before-layer(var(--surface-utilities-overlays-hover), var(--radius-default));

      border-radius: var(--radius-default, 3px);
    }

    &:active::before {
      @include before-layer(var(--surface-utilities-overlays-pressed), var(--radius-default));

      outline: none;
    }

    &:focus {
      border: none;
      color: var(--text-interaction-default);
      outline: 2px solid var(--stroke-interaction);
      outline-offset: 2px;
    }
  }

  &-tertiary {
    @include base-layer(var(--sol-button-background-color, var(--surface-base-default)));

    color: var(--text-base-default);

    &:hover::before {
      @include before-layer(var(--surface-utilities-overlays-hover), var(--radius-default));

      border-radius: var(--radius-default, 3px);
    }

    &:active::before {
      @include before-layer(var(--surface-utilities-overlays-pressed), var(--radius-default));

      outline: none;
    }

    &:focus {
      border: none;
      color: var(--text-interaction-default);
      outline: 2px solid var(--stroke-interaction);
      outline-offset: 2px;

      sol-icon {
        color: var(--stroke-interaction, #0c7ba1);
      }
    }

    &.sol-button--disabled,
    &.sol-button--disabled:hover {
      @include base-layer(var(--sol-button-background-color, var(--surface-base-default)));
    }
  }

  &--disabled {
    border: none;
    background: var(--surface-utilities-disabled-alpha);
    color: var(--text-utilities-disabled);
    cursor: not-allowed;

    sol-icon {
      color: var(--icon-utilities-disabled);
    }

    &.sol-button--no-text {
      background: var(--surface-utilities-disabled);

      sol-icon {
        color: var(--icon-utilities-disabled);
      }
    }

    &:hover::before,
    &:active::before {
      background: none;
      cursor: not-allowed;
    }
  }

  &--no-outline:focus {
    color: var(--text-base-default);
    outline-width: 0;
  }

  &-small {
    @extend %callout-default;

    padding: 6px var(--spacing-050, 8px);
    gap: var(--spacing-025, 4px);

    sol-icon {
      font-size: var(--scale-s3-size);
    }

    &.sol-button--no-text {
      padding: var(--spacing-025, 4px);

      sol-icon {
        font-size: var(--scale-s3-size);
      }
    }
  }

  &-medium {
    @extend %callout-default;

    padding: var(--spacing-050, 8px) var(--spacing-075, 12px);
    gap: var(--spacing-050, 8px);

    sol-icon {
      font-size: var(--scale-s4-size);
    }

    &.sol-button--no-text {
      padding: 6px;

      sol-icon {
        font-size: var(--scale-s5-size);
      }
    }
  }

  &-large {
    @extend %callout-emphasized;

    padding: var(--spacing-075, 12px) var(--spacing-100, 16px);
    gap: var(--spacing-050, 8px);
    line-height: 20px;

    sol-icon {
      font-size: var(--scale-s4-size);
    }

    &.sol-button--no-text {
      padding: 10px;

      sol-icon {
        font-size: var(--scale-s5-size);
      }
    }
  }
}
